/**
 * Utility for matching actual browser URLs against stored page URL patterns
 * that may contain dynamic segments (e.g., `:fileId`, `:id`).
 *
 * Example:
 *   stored pattern:  "/eno1/messages/samples/:fileId"
 *   actual URL:      "/eno1/messages/samples/abc-123"
 *   → match!
 */

/**
 * Check whether an actual URL matches a stored URL pattern.
 * A pattern segment that starts with ":" matches any single URL segment.
 *
 * Both URLs are compared case-insensitively after splitting on "/".
 */
export function urlMatchesPattern(actualUrl: string, pattern: string): boolean {
  // Wildcard patterns match any URL (used for Global module entries)
  if (pattern === '*' || pattern === '/global') return true;

  const actualParts = trimSlashes(actualUrl).split('/');
  const patternParts = trimSlashes(pattern).split('/');

  // Must have the same number of segments
  if (actualParts.length !== patternParts.length) return false;

  return patternParts.every((patternPart, i) => {
    // Dynamic segment matches anything
    if (patternPart.startsWith(':')) return true;
    return patternPart.toLowerCase() === actualParts[i].toLowerCase();
  });
}

/**
 * From a list of help contents, return those whose `pageurl` matches the
 * given actual URL (supporting dynamic segments in the stored pattern).
 */
export function filterHelpByUrl<T extends { pageurl: string; enabled?: boolean }>(
  contents: T[],
  actualUrl: string,
  onlyEnabled = true
): T[] {
  return contents.filter((content) => {
    if (onlyEnabled && !content.enabled) return false;
    return urlMatchesPattern(actualUrl, content.pageurl);
  });
}

// ── helpers ──────────────────────────────────────────────────────────────

function trimSlashes(url: string): string {
  return url.replace(/^\/+|\/+$/g, '');
}

